import { useState, useCallback } from 'react';

interface OptionsLoadScript {
  async?: boolean;
  defer?: boolean;
}

export enum LoadStatuses {
  LOADING = 'loading',
  LOADED = 'loaded',
  ERROR = 'error',
  IDLE = 'idle',
}

export const useLoadScript = (src: string, options: OptionsLoadScript) => {
  const [status, setStatus] = useState<LoadStatuses>(LoadStatuses.IDLE);

  const start = useCallback(() => {
    if (!src) {
      setStatus(LoadStatuses.IDLE);
      return;
    }

    setStatus(LoadStatuses.LOADING);

    const scriptElement = document.createElement('script');
    scriptElement.src = src;
    scriptElement.async = options.async ?? false;
    scriptElement.defer = options.defer ?? false;

    const onScriptLoad = () => {
      setStatus(LoadStatuses.LOADED);
    };

    const onScriptError = () => {
      scriptElement.remove();
      setStatus(LoadStatuses.ERROR);
    };

    scriptElement.addEventListener('load', onScriptLoad);
    scriptElement.addEventListener('error', onScriptError);

    document.head.appendChild(scriptElement);

    return () => {
      scriptElement.removeEventListener('load', onScriptLoad);
      scriptElement.removeEventListener('error', onScriptError);
    };
  }, [src, options.async, options.defer]);

  return {
    status,
    start,
  };
};
